import React, { useState } from "react";
import { FaTimesCircle } from "react-icons/fa";
import { BiCopy, BiErrorCircle } from "react-icons/bi";
import CopyToClipboard from "react-copy-to-clipboard";
import { toast } from "react-hot-toast";
import { TailSpin } from "react-loader-spinner";

import { getAssetImage } from "../../constant";
import { formatAmount } from "../../utils/FormatPerson";
import { useApiPatchMutation } from "../../lib/api";
import {
  canMarkPayoutAsPaid,
  formatTransactionDate,
  formatLedgerBalanceAmount,
  formatNairaFromKobo,
  formatTransactionRates,
  getLedgerBalanceRows,
  getTransactionAssetLabel,
  getTransactionNarration,
  getTransactionProviderLabel,
  getTransactionSourceLabel,
  getTransactionSourceKey,
  getTransactionTypeLabel,
  getUserTransactionAmountPlainText,
  resolveUserTransactionStatus,
  sourceBadgeClass,
  statusBadgeClass,
  transactionProviderData,
  transactionRecipient,
  utilityData,
  type UserActivityTransaction,
} from "./userActivityTransactionUtils";

function DetailRow({
  label,
  value,
  copyable,
  mono,
}: {
  label: string;
  value: string;
  copyable?: boolean;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  if (!value || value === "—") return null;

  const valueClass = `min-w-0 text-right text-[13px] text-[#DEE1E6] break-all ${
    mono ? "font-mono text-[12px]" : ""
  }`;

  const valueNode = copied ? (
    <span className={`${valueClass} text-[#359943]`}>Copied</span>
  ) : (
    <span className={valueClass}>{value}</span>
  );

  return (
    <div className="flex items-start justify-between gap-4 border-b border-[#2C363E]/60 py-3 last:border-b-0">
      <span className="shrink-0 text-[13px] font-medium text-[#869AA9]">
        {label}
      </span>
      <div className="flex min-w-0 max-w-[65%] items-start justify-end gap-1.5">
        {valueNode}
        {copyable ? (
          <CopyToClipboard
            text={value}
            onCopy={() => {
              setCopied(true);
              toast.success("Copied");
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            <button
              type="button"
              className="mt-0.5 shrink-0 text-[#617889] hover:text-[#BEE1FB]"
              aria-label={`Copy ${label}`}
            >
              <BiCopy className="text-[16px]" />
            </button>
          </CopyToClipboard>
        ) : null}
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-[#2C363E] bg-[#13181B] p-4">
      <h5 className="mb-2 text-[11px] font-bold uppercase tracking-wider text-[#617889]">
        {title}
      </h5>
      {children}
    </div>
  );
}

function LedgerBalanceTable({
  rows,
}: {
  rows: ReturnType<typeof getLedgerBalanceRows>;
}) {
  if (!rows.length) {
    return <p className="text-[13px] text-[#869AA9]">No balance snapshot</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="border-b border-[#2C363E]/60 text-left text-[#617889]">
            <th className="pb-2 pr-3 font-medium">Asset</th>
            <th className="pb-2 pr-3 font-medium">Before</th>
            <th className="pb-2 font-medium">After</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.asset}
              className="border-b border-[#2C363E]/40 last:border-b-0"
            >
              <td className="py-2 pr-3 font-semibold text-white">
                {row.asset}
              </td>
              <td className="py-2 pr-3 font-mono text-[#C6D0D7]">
                {formatLedgerBalanceAmount(row.before)}
              </td>
              <td className="py-2 font-mono text-[#C6D0D7]">
                {formatLedgerBalanceAmount(row.after)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

type UserTxnsModalProps = {
  onClose: () => void;
  txnDeets: UserActivityTransaction;
  email: string;
  firstname: string;
  lastName: string;
  onPayoutMarkedPaid?: () => void;
};

const UserTxnsModal = ({
  onClose,
  txnDeets,
  email,
  firstname,
  lastName,
  onPayoutMarkedPaid,
}: UserTxnsModalProps) => {
  const [markPaidError, setMarkPaidError] = useState("");
  const canMarkPaid = canMarkPayoutAsPaid(txnDeets);

  const markPaidMutation = useApiPatchMutation<
    unknown,
    { payoutIds: string[]; status: string }
  >("api/payouts/update-statuses", {
    onSuccess: () => {
      toast.success("Payout marked as completed");
      onPayoutMarkedPaid?.();
      onClose();
    },
    onError: (error) => {
      const msg =
        (error.response?.data as { message?: string } | undefined)?.message ??
        "Could not mark payout as paid.";
      setMarkPaidError(msg);
    },
  });

  const status = resolveUserTransactionStatus(txnDeets);
  const asset = getTransactionAssetLabel(txnDeets);
  const assetImage = getAssetImage(asset);
  const util = utilityData(txnDeets);
  const utilType = txnDeets.utility?.type;
  const meta = txnDeets.metadata;
  const fee = txnDeets.fee ?? txnDeets.fees;
  const ledger = txnDeets.ledger;
  const ledgerRows = getLedgerBalanceRows(ledger);
  const ledgerTx = ledger?.transaction;
  const sourceKey = getTransactionSourceKey(txnDeets);
  const recipient = transactionRecipient(txnDeets);
  const providerData = transactionProviderData(txnDeets);
  const providerLabel = getTransactionProviderLabel(txnDeets);
  const payoutSource = typeof txnDeets.source === "string" ? txnDeets.source : "";
  const linkedTransaction = txnDeets.transaction;
  const payoutLogs = Array.isArray(txnDeets.logs) ? txnDeets.logs : [];
  const rawSourceAddresses = providerData?.sourceAddresses;
  const sourceAddresses = Array.isArray(rawSourceAddresses)
    ? rawSourceAddresses.map(String)
    : [];

  const metaEntries =
    meta && typeof meta === "object"
      ? Object.entries(meta).filter(
          ([, v]) => v != null && typeof v !== "object"
        )
      : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-6 font-manrope backdrop-blur-sm">
      <div className="flex max-h-[min(90vh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-[#2C363E] bg-[#0D0D0D] shadow-2xl">
        <div className="flex shrink-0 items-start justify-between border-b border-[#2C363E] px-5 py-4">
          <div className="min-w-0 flex-1 pr-4">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-semibold tracking-wide ${sourceBadgeClass(
                  getTransactionSourceKey(txnDeets)
                )}`}
              >
                {getTransactionSourceLabel(txnDeets)}
              </span>
              <span className={statusBadgeClass(status.tone)}>
                {status.label}
              </span>
            </div>
            <h4 className="mt-2 font-sora text-[18px] font-bold text-white uppercase">
              {getTransactionTypeLabel(txnDeets)}
            </h4>
            <p className="mt-1 text-[12px] text-[#617889]">
              {formatTransactionDate(txnDeets)}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <FaTimesCircle className="cursor-pointer text-[24px] text-[#869AA9] hover:text-white" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5 scrollbar-thin scrollbar-track-[#0D0D0D] scrollbar-thumb-[#3A4852]">
          <div className="flex items-center gap-3 rounded-xl border border-[#2C363E] bg-[#13181B] p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#0D1115]">
              {assetImage ? (
                <img
                  src={assetImage}
                  alt={asset}
                  className="h-full w-full object-contain"
                />
              ) : (
                <span className="text-[11px] font-bold text-[#869AA9]">
                  {asset.slice(0, 3)}
                </span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-[#617889]">
                Amount
              </p>
              <p className="truncate text-[18px] font-bold text-[#BEE1FB]">
                {getUserTransactionAmountPlainText(txnDeets)}
              </p>
            </div>
          </div>

          <Section title="Overview">
            <DetailRow
              label="Transaction ID"
              value={txnDeets._id}
              copyable
              mono
            />
            {txnDeets.reference ? (
              <DetailRow
                label="Reference"
                value={txnDeets.reference}
                copyable
                mono
              />
            ) : null}
            {txnDeets.withdrawalId ? (
              <DetailRow
                label="Withdrawal ref"
                value={String(txnDeets.withdrawalId)}
                copyable
                mono
              />
            ) : null}
            {txnDeets.anchorPaymentId ? (
              <DetailRow
                label="Payment ID"
                value={String(txnDeets.anchorPaymentId)}
                copyable
                mono
              />
            ) : null}
            {txnDeets.txid ? (
              <DetailRow label="Tx ID" value={txnDeets.txid} copyable mono />
            ) : null}
            <DetailRow label="Asset" value={asset} />
            <DetailRow label="Description" value={getTransactionNarration(txnDeets)} />
            {fee != null && fee > 0 ? (
              <DetailRow label="Fee" value={formatNairaFromKobo(fee)} />
            ) : null}
            {txnDeets.payoutAmount != null && txnDeets.payoutAmount > 0 ? (
              <DetailRow
                label="Payout amount"
                value={`₦ ${formatAmount(txnDeets.payoutAmount)}`}
              />
            ) : null}
            {txnDeets.amountNGN != null && txnDeets.amountNGN > 0 ? (
              <DetailRow
                label="NGN equivalent"
                value={`₦ ${formatAmount(txnDeets.amountNGN)}`}
              />
            ) : null}
            {txnDeets.usdtAmount != null ? (
              <DetailRow
                label="USDT value"
                value={`${formatAmount(txnDeets.usdtAmount)} USDT`}
              />
            ) : null}
            {txnDeets.isAutomated != null ? (
              <DetailRow
                label="Automated"
                value={txnDeets.isAutomated ? "Yes" : "No"}
              />
            ) : null}
            {txnDeets.batchId ? (
              <DetailRow
                label="Batch ID"
                value={String(txnDeets.batchId)}
                copyable
                mono
              />
            ) : null}
          </Section>

          {sourceKey === "sell-deposit" ? (
            <Section title="Sell deposit">
              {providerLabel ? (
                <DetailRow label="Provider" value={providerLabel} />
              ) : null}
              {txnDeets.network ? (
                <DetailRow label="Network" value={txnDeets.network} />
              ) : null}
              {providerData?.status ? (
                <DetailRow
                  label="Provider status"
                  value={String(providerData.status)}
                />
              ) : null}
              {providerData?.hash ? (
                <DetailRow
                  label="On-chain hash"
                  value={String(providerData.hash)}
                  copyable
                  mono
                />
              ) : null}
              {providerData?.reference ? (
                <DetailRow
                  label="Provider reference"
                  value={String(providerData.reference)}
                  copyable
                  mono
                />
              ) : null}
              {providerData?.transactionId ? (
                <DetailRow
                  label="Provider transaction ID"
                  value={String(providerData.transactionId)}
                  copyable
                  mono
                />
              ) : null}
              {txnDeets.rates?.length ? (
                <DetailRow
                  label="Rates"
                  value={formatTransactionRates(txnDeets.rates)}
                />
              ) : null}
              {sourceAddresses.length > 0 ? (
                <DetailRow
                  label="Source address"
                  value={sourceAddresses[0]}
                  copyable
                  mono
                />
              ) : null}
            </Section>
          ) : null}

          {sourceKey === "payout" ? (
            <Section title="Payout">
              {payoutSource ? (
                <DetailRow label="Payout source" value={payoutSource} />
              ) : null}
              {providerLabel ? (
                <DetailRow label="Provider" value={providerLabel} />
              ) : null}
              {linkedTransaction?.id ? (
                <DetailRow
                  label="Linked transaction"
                  value={linkedTransaction.id}
                  copyable
                  mono
                />
              ) : null}
              {linkedTransaction?.type ? (
                <DetailRow
                  label="Linked tx type"
                  value={linkedTransaction.type}
                />
              ) : null}
              {canMarkPaid ? (
                <div className="mt-4 space-y-3 border-t border-[#2C363E]/60 pt-4">
                  <p className="text-[12px] leading-relaxed text-amber-100/90">
                    Confirm you have not already paid this manually. This action
                    cannot be reversed.
                  </p>
                  {markPaidError ? (
                    <div className="flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-3 text-[13px] font-medium text-red-200">
                      <BiErrorCircle className="shrink-0 text-xl" aria-hidden />
                      {markPaidError}
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setMarkPaidError("");
                      markPaidMutation.mutate({
                        payoutIds: [txnDeets._id],
                        status: "COMPLETED",
                      });
                    }}
                    disabled={markPaidMutation.isPending}
                    className={
                      markPaidMutation.isPending
                        ? "flex min-h-[44px] w-full cursor-not-allowed items-center justify-center rounded-xl border-2 border-[#2C363E] px-4 text-[14px] font-bold text-[#5C6B76]"
                        : "flex min-h-[44px] w-full items-center justify-center rounded-xl border-2 border-secondary_button px-4 text-[14px] font-bold text-secondary_button transition hover:bg-[#1C2227]"
                    }
                  >
                    {markPaidMutation.isPending ? (
                      <TailSpin color="#0B84CB" height={24} width={24} />
                    ) : (
                      "Mark as paid"
                    )}
                  </button>
                </div>
              ) : null}
            </Section>
          ) : null}

          {recipient ? (
            <Section title="Recipient">
              <DetailRow
                label="Account name"
                value={recipient.accountName ?? "—"}
              />
              <DetailRow
                label="Account number"
                value={recipient.accountNumber ?? "—"}
                copyable
              />
              <DetailRow label="Bank" value={recipient.bankName || "—"} />
              {recipient.bankCode ? (
                <DetailRow label="Bank code" value={recipient.bankCode} />
              ) : null}
            </Section>
          ) : null}

          {payoutLogs.length > 0 ? (
            <Section title="Payout logs">
              {payoutLogs.map((log, index) => (
                <DetailRow
                  key={log._id ?? `log-${index}`}
                  label={
                    log.time
                      ? formatTransactionDate({
                          ...txnDeets,
                          createdAt: log.time,
                        })
                      : `Log ${index + 1}`
                  }
                  value={log.comment ?? "—"}
                />
              ))}
            </Section>
          ) : null}

          {ledger ? (
            <Section title="Ledger">
              <DetailRow label="Entry type" value={ledger.type ?? "—"} />
              {ledgerTx?.type ? (
                <DetailRow label="Ledger tx type" value={ledgerTx.type} />
              ) : null}
              {ledgerTx?.asset ? (
                <DetailRow label="Ledger asset" value={ledgerTx.asset} />
              ) : null}
              {ledgerTx?.amount != null ? (
                <DetailRow
                  label="Ledger amount"
                  value={`${formatAmount(ledgerTx.amount)} ${ledgerTx.asset ?? ""}`.trim()}
                />
              ) : null}
              {ledgerTx?.fee != null && ledgerTx.fee > 0 ? (
                <DetailRow
                  label="Ledger fee"
                  value={`${formatAmount(ledgerTx.fee)} ${ledgerTx.asset ?? ""}`.trim()}
                />
              ) : null}
              {ledgerTx?.provider ? (
                <DetailRow label="Ledger provider" value={ledgerTx.provider} />
              ) : null}
              {ledger.timestamp ? (
                <DetailRow
                  label="Ledger timestamp"
                  value={formatTransactionDate({
                    ...txnDeets,
                    createdAt: ledger.timestamp,
                  })}
                />
              ) : null}
              {ledger._id ? (
                <DetailRow
                  label="Ledger ID"
                  value={ledger._id}
                  copyable
                  mono
                />
              ) : null}
              <div className="mt-3 border-t border-[#2C363E]/60 pt-3">
                <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-[#617889]">
                  Balance snapshot
                </p>
                <LedgerBalanceTable rows={ledgerRows} />
              </div>
            </Section>
          ) : null}

          {txnDeets.counterParty && !recipient ? (
            <Section title="Counterparty">
              <DetailRow
                label="Account name"
                value={txnDeets.counterParty.accountName ?? "—"}
              />
              <DetailRow
                label="Account number"
                value={txnDeets.counterParty.accountNumber ?? "—"}
                copyable
              />
              <DetailRow
                label="Bank"
                value={txnDeets.counterParty.bankName || "—"}
              />
            </Section>
          ) : null}

          {(txnDeets.from || txnDeets.to) && (
            <Section title="Swap">
              {txnDeets.from ? (
                <DetailRow
                  label="From"
                  value={`${txnDeets.from.amount ?? "—"} ${
                    txnDeets.from.asset ?? ""
                  }`}
                />
              ) : null}
              {txnDeets.to ? (
                <DetailRow
                  label="To"
                  value={`${txnDeets.to.amount ?? "—"} ${
                    txnDeets.to.asset ?? ""
                  }`}
                />
              ) : null}
            </Section>
          )}

          {(utilType || util) && (
            <Section title="Bill / utility">
              {utilType ? (
                <DetailRow
                  label="Category"
                  value={utilType.replace(/_/g, " ")}
                />
              ) : null}
              {util ? (
                <>
                  <DetailRow label="Phone" value={String(util.phone ?? "")} />
                  <DetailRow
                    label="Meter"
                    value={String(util.meter_number ?? "")}
                  />
                  <DetailRow
                    label="Vendor status"
                    value={String(util.transaction_status ?? util.status ?? "")}
                  />
                  <DetailRow
                    label="Token"
                    value={String(util.token ?? util.utilityToken ?? "")}
                    copyable
                  />
                  <DetailRow
                    label="Customer"
                    value={String(util.customer_name ?? "")}
                  />
                </>
              ) : null}
            </Section>
          )}

          {(txnDeets.toAddress || txnDeets.toDeexUser || txnDeets.network) && (
            <Section title="Transfer details">
              {txnDeets.network ? (
                <DetailRow label="Network" value={txnDeets.network} />
              ) : null}
              {txnDeets.toAddress ? (
                <DetailRow
                  label="To address"
                  value={txnDeets.toAddress}
                  copyable
                  mono
                />
              ) : null}
              {txnDeets.toDeexUser ? (
                <DetailRow
                  label="To Deex user"
                  value={txnDeets.toDeexUser}
                  copyable
                  mono
                />
              ) : null}
            </Section>
          )}

          {metaEntries.length > 0 ? (
            <Section title="Metadata">
              {metaEntries.map(([key, val]) => (
                <DetailRow key={key} label={key} value={String(val)} />
              ))}
            </Section>
          ) : null}

          <a
            href={`mailto:${email}`}
            className="flex h-12 items-center justify-center rounded-xl border border-[#0B84CB] text-[14px] font-semibold text-white transition-colors hover:bg-[#0B84CB]/10"
          >
            Contact {firstname} {lastName}
          </a>
        </div>
      </div>
    </div>
  );
};

export default UserTxnsModal;
